import React, { useMemo } from 'react'
import BaseYouMayAlsoLikeTile from './BaseYouMayAlsoLikeTile'
import ProductTypeLabel from 'components/Luxkit/Label/ProductTypeLabel'
import FormatCurrency from 'components/Common/FormatCurrency'
import { pluralizeToString } from 'lib/string/pluralize'
import OfferUrgencyLabels from 'components/Common/Labels/OfferUrgencyLabels'
import Caption from 'components/Luxkit/Typography/Caption'
import Pill from 'components/Luxkit/Pill'
import { MINIMUM_DISCOUNT_TO_SHOW_PERCENTAGE_BADGE } from 'constants/content'
import getValueOffPercent from 'lib/offer/getValueOffPercent'
import Group from 'components/utils/Group'
import OfferBadgeTag from 'components/Common/OfferBadgeTag'
import LabelGroup from 'components/Luxkit/Label/LabelGroup'
import { useOfferBestPrice } from 'hooks/Offers/useOfferBestPrice'
import PriceRowPriceCaption from 'components/Luxkit/PricePoints/PriceRowPriceCaption'
import PriceRowPrice from 'components/Luxkit/PricePoints/PriceRowPrice'

interface Props {
  offer: App.OfferSummary;
  filters?: App.OfferListFilters;
}

function HotelYouMayAlsoLikeTile({ offer, filters }: Props) {
  const pkg = useOfferBestPrice(offer, filters)

  const shouldShowValue = !!pkg?.shouldDisplayValue && pkg.value > pkg.price
  const shouldShowPricing = !!pkg?.price
  const discount: number | undefined = useMemo(() => {
    if (!pkg) {
      return undefined
    }
    const dis = getValueOffPercent(pkg.value, pkg.price)
    if (dis < MINIMUM_DISCOUNT_TO_SHOW_PERCENTAGE_BADGE) {
      return undefined
    }
    return dis
  }, [pkg])

  return (
    <BaseYouMayAlsoLikeTile
      image={offer.image}
      location={[offer.locationHeading, offer.locationSubheading].filter(t => t).join(', ')}
      title={offer.property?.name ?? offer.name}
      subtitle={offer.property?.name ? offer.name : ''}
      walledGarden={offer.walledGarden}
    >
      <BaseYouMayAlsoLikeTile.ImageBadges>
        <ProductTypeLabel productType={offer.productType} />
      </BaseYouMayAlsoLikeTile.ImageBadges>
      <BaseYouMayAlsoLikeTile.ContentTags>
        <LabelGroup>
          <OfferUrgencyLabels offer={offer} />
          {offer.badge && <OfferBadgeTag badge={offer.badge} />}
        </LabelGroup>
      </BaseYouMayAlsoLikeTile.ContentTags>
      <BaseYouMayAlsoLikeTile.PriceDetails>
        {shouldShowPricing && (<>
          <PriceRowPriceCaption>
            {pluralizeToString('night', pkg.duration)} from
          </PriceRowPriceCaption>
          <PriceRowPrice
              size="M"
              price={Math.ceil(pkg.price + (pkg.surcharge ?? 0) + (pkg.propertyFees ?? 0))}
              saleUnit={offer.saleUnit}
              taxesAndFees={pkg.taxesAndFees}
            />
          {shouldShowValue && (
            <Group direction="horizontal" verticalAlign="center" gap={4}>
              <Caption variant="medium">Valued up to <FormatCurrency value={Math.ceil(pkg.value)} /></Caption>
              {discount && (
                <Pill kind="primary" variant="success">
                  Save up to {discount}%
                </Pill>
              )}
            </Group>
          )}
        </>)}
      </BaseYouMayAlsoLikeTile.PriceDetails>
    </BaseYouMayAlsoLikeTile>
  )
}

export default HotelYouMayAlsoLikeTile
